import { BASEURL, HEADERS } from "components/Utils/Constants";
// import moment from "moment-timezone";
import { MIX_RESOLVED_TARGETS } from "./MixGraphQL";

// Check if the current environment is development
const isDevelopment = () => window.location.hostname !== "dashboard.pickaxe.ai";

// Append development flag if in development environment
const appendDevelopmentFlag = (url) => {
  return isDevelopment() ? `${url}${url.includes("?") ? "&" : "?"}` : url;
};

// Get absolute URL by appending BASEURL if necessary
const absolute = (uri) => (uri.includes("http") ? uri : `${BASEURL}${uri}`);

// Fetch mix model version from localStorage
const getMixModelVersion = () => {
  let mixModelVersion = localStorage.getItem("mixModelVersion");
  return mixModelVersion === "null" ? "" : mixModelVersion;
};

/**
 * Fetch data from the server with appropriate model version and environment flag
 * @param {string} url - The API endpoint URL
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const fetchMixData = (url) => {
  const mixModelVersion = getMixModelVersion();
  const signAggregation = url.includes("?") ? "&" : "?";
  let finalUrl = absolute(
    appendDevelopmentFlag(
      url +
        (mixModelVersion
          ? `${signAggregation}model_version_id=${encodeURIComponent(
              mixModelVersion
            )}`
          : "")
    )
  );

  return fetch(finalUrl, {
    method: "GET",
    headers: HEADERS(window.localStorage.token),
  }).then((response) => response.json());
};

/**
 * Fetch data for Spend Acquire card
 * @param {object} scenario - Scenario details
 * @param {object} mixType - Mix type data
 * @param {string} projectId - Project ID
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getSpendAcquireData = (
  scenario,
  mixType,
  projectId,
  dateRange
) => {
  const slug = localStorage.defaultTenantSlug;
  let url = `/dashboard/${slug}/mmm-spend-acquire?scenario_name=${encodeURIComponent(
    scenario?.name
  )}${
    mixType ? "&mix_type=" + encodeURIComponent(mixType.value) : ""
  }&project_id=${projectId}`;

  if (dateRange) {
    url += `&from=${encodeURIComponent(
      dateRange.from
    )}&to=${encodeURIComponent(dateRange.to)}`;
  }
  return fetchMixData(url);
};

/**
 * Fetch data for Channel Analysis chart
 * @param {object} scenario - Scenario details
 * @param {string} metric - Selected metric target ID
 * @param {string} projectId - Project ID
 * @param {object} dateRange - Optional date range object with from and to properties
 * @param {boolean} includeTarget - Whether to include target parameters
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getChannelAnalysisData = (
  scenario,
  metric,
  projectId,
  dateRange,
  includeTarget = false
) => {
  const slug = localStorage.defaultTenantSlug;
  let url = `/dashboard/${slug}/mmm-channel-analysis?scenario_name=${encodeURIComponent(
    scenario?.name
  )}&project_id=${encodeURIComponent(projectId)}`;

  // Only include target parameters if includeTarget is true
  if (includeTarget && metric) {
    url += `&target_id=${encodeURIComponent(metric)}`;
  }

  if (dateRange) {
    url += `&from=${encodeURIComponent(
      dateRange.from
    )}&to=${encodeURIComponent(dateRange.to)}`;
  }

  return fetchMixData(url);
};

/**
 * Fetch channel list data for value spend, conversions, etc.
 * @param {object} scenario - Scenario details
 * @param {string} projectId - Project ID
 * @param {string} targetId - Target ID
 * @param {object} dateRange - Optional date range object with from and to properties
 * @param {string} funnel - Funnel parameter
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getChannelListData = (
  scenario,
  projectId,
  dateRange,
  targetIds,
  funnel
) => {
  const slug = localStorage.defaultTenantSlug;
  let url = `/dashboard/${slug}/mmm-channel-list?scenario_name=${encodeURIComponent(
    scenario?.name
  )}&project_id=${encodeURIComponent(projectId)}&version=test`;

  if (targetIds && targetIds.length > 0) {
    // Handle both single target ID (string) and multiple target IDs (array)
    const targetIdParam = Array.isArray(targetIds)
      ? targetIds.join(",")
      : targetIds;
    url += `&target_id=${encodeURIComponent(targetIdParam)}`;
  }

  if (funnel) {
    url += `&funnel=${encodeURIComponent(funnel)}`;
  }

  if (dateRange) {
    url += `&from=${encodeURIComponent(
      dateRange.from
    )}&to=${encodeURIComponent(dateRange.to)}`;
  }
  return fetchMixData(url);
};

/**
 * Fetch detailed data for a specific channel in read/edit mode
 * @param {object} scenario - Scenario details
 * @param {object} channel - Channel data
 * @param {boolean} isEditing - Editing mode flag
 * @param {string} projectId - Project ID
 * @param {string} targetId - Target ID for filtering data
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getChannelListDetailData = (
  scenario,
  channel,
  isEditing,
  projectId,
  targetId
) => {
  const slug = localStorage.defaultTenantSlug;
  const simulationPayload = isEditing
    ? `&average_weekly_spend=${encodeURIComponent(
        channel.histBudgetAvgValue
      )}&slider_weekly_spend=${encodeURIComponent(channel.averageWeeklySpend)}`
    : `&average_weekly_spend=${encodeURIComponent(
        channel.averageWeeklySpend
      )}&slider_weekly_spend=${encodeURIComponent(channel.averageWeeklySpend)}`;

  const url = `/dashboard/${slug}/mmm-channel-details?scenario_name=${encodeURIComponent(
    scenario?.name
  )}&channel_name=${encodeURIComponent(
    channel.friendlyName
  )}${simulationPayload}&project_id=${encodeURIComponent(projectId)}${
    targetId ? `&target_id=${encodeURIComponent(targetId)}` : ""
  }`;
  return fetchMixData(url);
};

/**
 * Format mix editing data into a payload object
 * @param {object} mixEditData - The data to format
 * @param {array} selectedTargetIds - Array of selected target IDs (first element used as target_id)
 * @param {object} scenario - Scenario object containing id and other details
 * @returns {object|null} - The formatted payload or null if no data
 */
export const getMixPayload = (
  mixEditData,
  selectedTargetIds = [],
  scenario = null,
  dateRange = null
) => {
  if (!mixEditData) {
    return null;
  }

  // Check for both 'channel' and 'channelList' to handle different data structures
  const channels = mixEditData.channel || mixEditData.channelList;

  if (!channels) {
    return null;
  }

  const payload = {
    goal: mixEditData.currentGoal,
    budget: mixEditData.currentBudget,
    channel: channels.map((channel) => ({
      id: channel.id,
      value: channel.weeklySpend?.averageWeeklySpend || 0,
    })),
  };

  // Add target information if available
  if (selectedTargetIds && selectedTargetIds.length > 0) {
    payload.target_id = selectedTargetIds[0];
  }

  // Add scenario information if available
  if (scenario && scenario.id) {
    payload.scenarioId = scenario.id;
    payload.scenarioName = scenario.name;
  }

  // Add date range information if available
  if (dateRange && dateRange.from && dateRange.to) {
    payload.dateRange = {
      from: dateRange.from,
      to: dateRange.to,
    };
  }

  return payload;
};

/**
 * Format mix editing data into a payload object (grouped funnel version)
 * @param {object} mixEditData - The data to format (expects channelList as object with funnel keys)
 * @param {array} selectedTargetIds - Array of selected target IDs (first element used as target_id)
 * @param {object} scenario - Scenario object containing id and other details
 * @returns {object|null} - The formatted payload or null if no data
 */
export const getMixEditPayload = (
  mixEditData,
  selectedTargetIds = [],
  scenario = null,
  dateRange = null
) => {
  if (!mixEditData) return null;
  let channels = [];
  if (Array.isArray(mixEditData.channel)) {
    channels = mixEditData.channel;
  } else if (
    mixEditData.channelList &&
    typeof mixEditData.channelList === "object"
  ) {
    channels = Object.values(mixEditData.channelList).flat();
  }
  const payload = {
    goal: mixEditData.currentGoal,
    budget: mixEditData.currentBudget,
    channel: channels.map((channel) => ({
      id: channel.id,
      value: channel.weeklySpend?.averageWeeklySpend || 0,
    })),
  };

  // Add target information if available
  if (selectedTargetIds && selectedTargetIds.length > 0) {
    payload.target_id = selectedTargetIds[0];
  }

  // Add scenario information if available
  if (scenario && scenario.id) {
    payload.scenarioId = scenario.id;
    payload.scenarioName = scenario.name;
  }

  // Add date range information if available
  if (dateRange && dateRange.from && dateRange.to) {
    payload.dateRange = {
      from: dateRange.from,
      to: dateRange.to,
    };
  }

  return payload;
};

/**
 * Fetch data for editing mix models
 * @param {object} scenario - Scenario details
 * @param {object} currentProject - Current project data
 * @param {object} mixEditData - Mix editing data
 * @param {object} mixType - Mix type data
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getMixDataEdit = (
  scenario,
  currentProject,
  mixEditData,
  mixType,
  targetId,
  dateRange // add dateRange param
) => {
  const payload = getMixEditPayload(
    mixEditData,
    targetId ? [targetId] : [],
    scenario,
    dateRange
  );

  const payloadString = payload
    ? `&in_payload=${JSON.stringify(
        payload
      )}&save_state=true&reset=${encodeURIComponent(
        mixEditData.ignoreChannelList
      )}`
    : "";

  const slug = localStorage.defaultTenantSlug;
  const tenantId = localStorage.getItem("defaultTenantID");
  const projectId = currentProject.projectObj.id;

  let url = `/dashboard/${slug}/mmm-mixer?scenario_name=${encodeURIComponent(
    scenario?.name
  )}&tenant_id=${encodeURIComponent(tenantId)}&project_id=${encodeURIComponent(
    projectId
  )}${payloadString}${
    mixType ? "&mix_type=" + encodeURIComponent(mixType.value) : ""
  }`;

  if (targetId) {
    url += `&target_id=${encodeURIComponent(targetId)}`;
  }

  if (dateRange) {
    url += `&from=${encodeURIComponent(dateRange.from)}&to=${encodeURIComponent(dateRange.to)}`;
  }

  return fetchMixData(url);
};

/**
 * Fetch available targets for mix analysis
 * @param {object} apolloClient - Apollo Client instance
 * @param {string} tenantId - Tenant ID
 * @param {string} projectId - Project ID
 * @param {string} scenarioId - Scenario ID
 * @returns {Promise} - A promise that resolves to the fetched targets
 */
export const getMixResolvedTargets = async (
  apolloClient,
  tenantId,
  projectId,
  scenarioId
) => {
  const { data } = await apolloClient.query({
    query: MIX_RESOLVED_TARGETS,
    variables: {
      tenantId,
      projectId,
      scenarioId,
    },
  });
  return data.mixResolvedTargets;
};

/**
 * Fetch data for Mix Summary card
 * @param {object} scenario - Scenario details
 * @param {object} mixType - Mix type data
 * @param {string} projectId - Project ID
 * @param {object} dateRange - Optional date range object with from and to properties
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getMixSummary = (scenario, mixType, projectId, dateRange) => {
  const slug = localStorage.defaultTenantSlug;
  let url = `/dashboard/${slug}/mmm-spend-summary?scenario_name=${encodeURIComponent(
    scenario?.name
  )}${
    mixType ? "&mix_type=" + encodeURIComponent(mixType.value) : ""
  }&project_id=${projectId}`;

  if (dateRange) {
    url += `&from=${encodeURIComponent(
      dateRange.from
    )}&to=${encodeURIComponent(dateRange.to)}`;
  }
  return fetchMixData(url);
};

/**
 * Fetch data for Mix Breakdown analysis
 * @param {object} scenario - Scenario details
 * @param {string} projectId - Project ID
 * @param {object} dateRange - Optional date range object with from and to properties
 * @param {string} targetId - Optional target ID for filtering data
 * @param {object} mixType - Optional mix type data
 * @returns {Promise} - A promise that resolves to the fetched data
 */
export const getMixBreakdown = (
  scenario,
  projectId,
  dateRange,
  targetId,
  mixType
) => {
  const slug = localStorage.defaultTenantSlug;
  let url = `/dashboard/${slug}/mmm-breakdown?scenario_name=${encodeURIComponent(
    scenario?.name
  )}&project_id=${encodeURIComponent(projectId)}`;

  if (targetId) {
    url += `&target_id=${encodeURIComponent(targetId)}`;
  }

  if (mixType) {
    url += `&mix_type=${encodeURIComponent(mixType.value)}`;
  }

  if (dateRange) {
    url += `&from=${encodeURIComponent(
      dateRange.from
    )}&to=${encodeURIComponent(dateRange.to)}`;
  }

  return fetchMixData(url);
};

/**
 * Generate and download mix report
 * @param {string} tenantId - Tenant ID
 * @param {string} projectId - Project ID
 * @param {string} targetId - Target ID
 * @returns {Promise} - A promise that resolves to the download URL or blob
 */
export const generateMixReport = async (tenantId, projectId, targetId) => {
  const url = `/mix/report?tenant_id=${encodeURIComponent(tenantId)}&project_id=${encodeURIComponent(projectId)}&target_id=${encodeURIComponent(targetId)}`;

  const finalUrl = absolute(url);

  // Timeout via AbortController for robustness
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  try {
    const response = await fetch(finalUrl, {
      method: "GET",
      headers: HEADERS(window.localStorage.token),
      signal: controller.signal,
      redirect: "follow",
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => "");
      throw new Error(
        `Failed to generate report (${response.status}): ${response.statusText} ${errText}`.trim()
      );
    }

    const contentType = response.headers.get("content-type") || "";

    // If server returns JSON with a URL
    if (contentType.includes("application/json")) {
      const data = await response.json();
      const name =
        data.filename && /\.csv$/i.test(data.filename)
          ? data.filename
          : data.filename
            ? `${data.filename}.csv`
            : "mix-report.csv";
      return { downloadUrl: data.url || data.downloadUrl, filename: name };
    }

    // Otherwise treat as CSV blob
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);

    // Filename: prefer Content-Disposition, else check redirected URL params
    let filename = "mix-report.csv";
    let cd = response.headers.get("content-disposition");
    if (!cd) {
      try {
        const u = new URL(response.url);
        const cdParam =
          u.searchParams.get("response-content-disposition") ||
          u.searchParams.get("content-disposition");
        if (cdParam) cd = decodeURIComponent(cdParam);
      } catch (e) {}
    }
    if (cd) {
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
      if (m) {
        // Use the filename provided by the server as-is (decoded), do not change/append extensions
        const provided = decodeURIComponent((m[1] || m[2]).trim());
        if (provided) filename = provided;
      }
    }

    return { downloadUrl, filename, blob };
  } finally {
    clearTimeout(timeoutId);
  }
};
